// client/src/api/ApiPlayoffs.js
// Import the flex module for making HTTP requests
import flex from './FlexFetch'
import { unwrapStrictError } from './contractUtils'

// Base endpoint for playoff-related API requests
const playoffs_endpoint = '/api/1.0/playoffs/'

// TODO: Remove flattenStrictObjectPayload once all playoff component call sites
// are updated to access result.data.<prop> directly (unwrapStrictData pattern).
// See: https://github.com/hkauffman3/flex-league/issues (track as follow-up issue)
function flattenStrictObjectPayload(payload) {
  if (
    payload?.success === true &&
    Object.prototype.hasOwnProperty.call(payload, 'data') &&
    payload.data &&
    typeof payload.data === 'object' &&
    !Array.isArray(payload.data)
  ) {
    return {
      ...payload,
      ...payload.data,
    }
  }

  return payload
}

// Helper function to standardize error handling
const handleApiError = (error, defaultMessage) => {
  const normalizedError = unwrapStrictError(error.response?.data)
  const errorMessage =
    normalizedError?.message ||
    normalizedError?.error ||
    error.message ||
    defaultMessage
  throw new Error(errorMessage)
}

// Start playoffs for a league
async function startPlayoffs(leagueId, leagueLevelId = null) {
  try {
    // Use camelCase command name to match server export: startPlayoffs
    const body = { league_id: leagueId }
    if (leagueLevelId != null) {
      body.league_level_id = leagueLevelId
    }
    const response = await flex.flexFetch(playoffs_endpoint + 'startPlayoffs', {
      method: 'POST',
      body: JSON.stringify(body),
    })
    return flattenStrictObjectPayload(await flex.handleResponse(response))
  } catch (error) {
    handleApiError(error, 'Failed to start playoffs')
  }
}

// Get bracket for a league
async function getBracket(leagueId, leagueLevelId = null) {
  try {
    const levelQuery =
      leagueLevelId != null ? `&league_level_id=${leagueLevelId}` : ''
    // Use camelCase command name to match server export: getBracket
    const response = await flex.flexFetch(
      `${playoffs_endpoint}getBracket?league_id=${leagueId}${levelQuery}`,
      {
        method: 'GET',
      },
    )
    return flattenStrictObjectPayload(await flex.handleResponse(response))
  } catch (error) {
    handleApiError(error, 'Failed to get bracket')
  }
}

// Update seeds for a league
async function updateSeed(leagueId, seedUpdates) {
  try {
    // Use camelCase command name to match server export: updateSeed
    const response = await flex.flexFetch(playoffs_endpoint + 'updateSeed', {
      method: 'POST',
      body: JSON.stringify({
        league_id: leagueId,
        seed_updates: seedUpdates,
      }),
    })
    return flattenStrictObjectPayload(await flex.handleResponse(response))
  } catch (error) {
    handleApiError(error, 'Failed to update seeds')
  }
}

// Report result for a playoff match
async function reportResult(matchId, setScores, options = {}) {
  try {
    const body = {
      match_id: matchId,
      set_scores: setScores,
    }
    // Add optional walkover/forfeit flags if provided
    if (options.walkover) {
      body.walkover = true
      body.winner_player_id = options.winnerPlayerId
    }
    if (options.forfeit) {
      body.forfeit = true
      body.winner_player_id = options.winnerPlayerId
    }
    const response = await flex.flexFetch(playoffs_endpoint + 'reportResult', {
      method: 'POST',
      body: JSON.stringify(body),
    })
    return flattenStrictObjectPayload(await flex.handleResponse(response))
  } catch (error) {
    handleApiError(error, 'Failed to report match result')
  }
}

// Publish bracket (transition from playoffs_setup to playoffs)
async function publishBracket(leagueId, leagueLevelId = null) {
  try {
    const body = { league_id: leagueId }
    if (leagueLevelId != null) {
      body.league_level_id = leagueLevelId
    }
    const response = await flex.flexFetch(
      playoffs_endpoint + 'publishBracket',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    )
    return flattenStrictObjectPayload(await flex.handleResponse(response))
  } catch (error) {
    handleApiError(error, 'Failed to publish bracket')
  }
}

// Get playoff eligibility information for a league
async function getPlayoffEligibility(leagueId, leagueLevelId = null) {
  try {
    const levelQuery =
      leagueLevelId != null ? `&league_level_id=${leagueLevelId}` : ''
    const response = await flex.flexFetch(
      `${playoffs_endpoint}getPlayoffEligibility?league_id=${leagueId}${levelQuery}`,
      {
        method: 'GET',
      },
    )
    return flattenStrictObjectPayload(await flex.handleResponse(response))
  } catch (error) {
    handleApiError(error, 'Failed to get playoff eligibility')
  }
}

// Skip playoffs and mark league as completed
async function skipPlayoffs(leagueId) {
  try {
    const response = await flex.flexFetch(`${playoffs_endpoint}skipPlayoffs`, {
      method: 'POST',
      body: JSON.stringify({ league_id: leagueId }),
    })
    return flattenStrictObjectPayload(await flex.handleResponse(response))
  } catch (error) {
    handleApiError(error, 'Failed to skip playoffs')
  }
}

// Exports the API functions for use in other modules
const ApiPlayoffs = {
  startPlayoffs,
  getBracket,
  updateSeed,
  reportResult,
  publishBracket,
  getPlayoffEligibility,
  skipPlayoffs,
}

export default ApiPlayoffs
